// WHY a `pending` task is not moving, as a dispatcher pass stamped it
// (`tasks.hold_reason`). See `../task.ts` for the file docblock.

import { z } from 'zod';

/**
 * WHY a `pending` task is not moving, when the answer is not "queued behind
 * other work" (#1899). Stored on `tasks.hold_reason`, cleared the moment the
 * hold lifts. Text + Zod-at-read, the same stance as `status` and
 * {@link TaskFailureKind} — `failure_kind` answers "why is this row failed",
 * this one answers "why is this row still pending".
 *
 * Each value is one arm — one condition, one write site, one clear — never a
 * taxonomy designed up front. ONE MEMBER IS NOT (#6237): {@link BoxFaultExcluded} names
 * two conditions with two clears, a box-fault exclusion on a clock and a decode refusal
 * waiting for a newer runner release. Its docblock says why they share a value.
 *
 * THE PROPERTY, stated instead of a count, with the enumeration behind it:
 * EVERY member is a PENDING-time reason that a dispatcher pass both STAMPS and
 * RETRACTS over pending rows, each pass named with the seam that does both halves
 * (`packages/db/src/queries/tasks.ts`). NO MEMBER IS AN EXCEPTION TODAY. The one property
 * that admits one is READERS FIRST: a new member of a stored closed set ships its vocabulary
 * one release ahead of its writer (`docs/standards/stored-closed-sets.md`), so for that
 * release nothing stamps it, and its row cites `writer pending (#NNNN)`, the issue that writes
 * it, until that issue rewrites the row in the diff that adds the pass. The three members that
 * did so have writers now: {@link KeyReserved} (#6711, written by #6704's
 * `markKeyReservedHolds`), {@link CiPending} (#6718, written by its writers release) and
 * {@link NoRoleMachine} (#7008, written by #6756's `markNoRoleMachineHolds`). `hold-enumeration.test.ts` admits no
 * `writer pending` spelling while none is needed; the next readers-first member adds its own,
 * held to that member alone. The enumeration:
 *
 *   {@link NoUsableKeys}          `markTaskHolds`
 *   {@link SeatCapped}            `markTaskHolds`
 *   {@link PoolCooling}           `markTaskHolds` (account-wide) + `markPoolCoolingHolds` (per task)
 *   {@link AreaBusy}              `markAreaHolds`
 *   {@link NoConverseCapacity}    `markNoConverseCapacityHolds`
 *   {@link NoServiceCapacity}     `markNoServiceCapacityHolds`
 *   {@link NoCapableBox}          `markNoCapableBoxHolds`
 *   {@link RepoNotOnboarded}      `markRepoNotOnboardedHolds`
 *   {@link UpstreamGroupUnmerged} `markUpstreamGroupHolds`
 *   {@link BoxFaultExcluded}      `markBoxFaultExclusionHolds`
 *   {@link ModeDisabled}          `markModeDisabledHolds`
 *   {@link CredentialCooling}     `markCredentialCoolingHolds`
 *   {@link KeySaturated}          `markKeySaturatedHolds`
 *   {@link KeyReserved}           `markKeyReservedHolds`
 *   {@link SelectionWaiting}      `markSelectionWaitingHolds`
 *   {@link NoScreenKey}           `markNoScreenKeyHolds` + `failOrRetryTask` (a `screen_key_unusable` run)
 *   {@link ReviewAwaitingModel}   `markReviewAwaitingModelHolds`
 *   {@link ProviderKeyRefused}    `failOrRetryTask`
 *   {@link ProviderCapacity}      `failOrRetryTask`
 *   {@link RepoEnvFault}          `failOrRetryTask` (a `repo_env_down` or `box_baseline_red` run)
 *   {@link ResumeBackoff}         `parkExhaustedTask`
 *   {@link PrQuietWindow}         `failOrRetryTask`
 *   {@link CiPending}             `failOrRetryTask` (a box's `review_ci_pending` run) + `holdReviewForCi` (the review mint) + `reviveCiRedReview` with `holdUntil` (the CI wake's re-hold), `packages/db/src/queries/tasks/ci-pending.ts`; retracted by `releaseCiPendingHold`
 *   {@link EnvFaultExhausted}     `holdExhaustedTask`
 *   {@link ErrorIssuePending}     `linkErrorIssueTasks` (retraction; the stamp is the triage mint)
 *   {@link NoRoleMachine}         `markNoRoleMachineHolds`
 *   {@link PlanPaused}            `markPlanPausedHolds`
 *   {@link MachinePinned}         `markMachinePinnedHolds`
 *
 * `ResumeBackoff` is stamped by the failure write's park step (`parkExhaustedTask`, in the same
 * transaction as `failOrRetryTask`) and retracted by the claim or an operator redispatch.
 * `EnvFaultExhausted` is its sibling: the failure write's hold step (`holdExhaustedTask`), same
 * transaction, same two retractions.
 *
 * TWO ROWS ARE NOT DISPATCHER PASSES, and the exception is structural rather than a
 * gap: a WINDOWED FAILURE HOLD is stamped by the failure write itself
 * (`failOrRetryTask`, reading `taskFailureRependHold`), because the fact it records
 * (a provider refused this task's key, the shared pool refused it for capacity, or the
 * screen could not answer for its text) exists only when a box reports the failure, never on a
 * dispatcher tick. Its retraction is the claim, which clears `hold_reason` when the lapsed window
 * lets a box take the row — plus, on the two members that wait for a KEY (`ProviderKeyRefused`,
 * and `NoScreenKey` when a `screen_key_unusable` run stamped it), `releaseHoldsOnKeyValid` on a
 * key turning `valid`. The capacity member has NO key wake (the key is fine; its own docblock states it): for a row
 * that STAYS `pending`, the clock is the only thing that ever clears the hold — the claim retracts the
 * stamp when the lapsed window lets a box take the row. It clears nothing else: a capacity-held row that
 * leaves `pending` by another door (cancel, fail) keeps the stamp, exactly as "the column outlives the
 * wait" says of the column generally. A `mark*Holds` twin was written for it and deleted in
 * review: it had no caller. The guard admits `failOrRetryTask` only for a member
 * `taskFailureRependHold` returns from a kind some box can REPORT, and `linkErrorIssueTasks`
 * only for {@link ErrorIssuePending} (`hold-enumeration.test.ts`; see the third correction
 * below).
 *
 * {@link ErrorIssuePending} IS NOT A DISPATCHER PASS EITHER: the triage mint stamps it and the
 * error-issue lane retracts it (`linkErrorIssueTasks`), because the fact it waits on is a filing,
 * not a box's tick.
 *
 * The passes are named and NOT line-numbered, on purpose: a line offset is
 * falsified by the next insertion into that file — this docblock shipped four of
 * them and every one was stale within a day of the functions beside them moving —
 * while a function name is greppable and moves with the code.
 *
 * Each pass clears only the value it OWNS — `markTaskHolds` states the rule for
 * all of them ("A pass may only retract its own verdict") — because a pass that
 * cleared another's would wipe a live fact every tick and re-emit the account's
 * whole pending set on the `tasks` SSE stream.
 *
 * TWO CORRECTIONS ARE RECORDED HERE. A tally falsifies the moment a member lands (this
 * paragraph once said "two of the three" over five), which is why the property is what a
 * reader re-checks against the list. And {@link NoUsableKeys} was once declared to have "NO
 * clear site anywhere in the tree", by grepping its token and reading the `reason:` field of
 * a `markTaskHolds(tx, { held, admitted, reason })` call as a bare stamp. A grep sees the
 * token; only the enclosing call says which half it is, and `markTaskHolds` does both.
 *
 * THE THIRD CORRECTION IS THE ONE THAT MADE THE LIST A GUARD'S INPUT. The
 * enumeration above stood at NINE rows while this enum held TEN:
 * {@link NoServiceCapacity} (#2919) landed with its member, its writer and its
 * stall kind, and never reached the list. Nothing was broken — the property held
 * for it exactly as stated — but the EVIDENCE for a universal had quietly stopped
 * being the enum, which is the same defect as a tally, arriving by a slower route.
 * `hold-enumeration.test.ts` now decides the list from `Object.keys` of this
 * object, in both directions and with a corpus floor, so the next member either
 * appears above or fails there. Do not hand-maintain it; it is that guard's input.
 *
 * A member that a `failed` row carries does NOT belong here. `dependency_failed`
 * was one until 2026-08-29 — see the type's own contract above: this column
 * answers "why is this row still pending", and {@link TaskFailureKind} answers
 * "why is this row failed".
 *
 * "A PENDING TASK WITH A NULL HERE IS IMPOSSIBLE" IS FALSE, AND STATING WHERE IT
 * STOPS IS THE POINT OF THIS PARAGRAPH. Two populations keep the column NULL and
 * only ONE of them was ever a defect. Measured on prod 2026-09-01 — 35 pending
 * rows, 12 carrying `area_busy` and 23 carrying nothing:
 *
 *   - 17 of the 23 were held by a claim-gate fragment with NO stamping pass
 *     ({@link RepoNotOnboarded}, 5 of them also {@link UpstreamGroupUnmerged}).
 *     That WAS the defect and those two members close it: a live pass reached
 *     those rows every tick — 10 boxes online on the account, 12 sibling rows
 *     stamped `area_busy` by the very same empty-claim pass — and dropped the
 *     verdict on the floor.
 *   - the other 6 are on an account with ZERO online boxes. Every writer of this
 *     column runs inside a per-BOX dispatcher pass, so an account with no box
 *     gets no pass, and a member minted for it would have no producer that can
 *     ever fire — the dead-producer shape this repo keeps paying for. It is
 *     answered instead by {@link TaskStallKind.NoBoxOnAccount}, DERIVED at read
 *     time from the account's runner rows, which is the whole reason that second
 *     vocabulary exists beside this one.
 *
 * So the reachable invariant, and the one
 * `packages/db/src/queries/claim-gate-hold-writer.test.ts` enforces, is narrower
 * and mechanical: EVERY named fragment `claimNextReady` narrows its candidate
 * `where` with is also read by a `mark*Holds` writer, so no gate can withhold a
 * pending row from a live pass without the row saying so.
 */
export const TaskHoldReason = {
  /**
   * The account has a key pool but ZERO usable keys right now (all cooled or
   * exhausted), so the assigner skipped the claim rather than dispatch a doomed
   * no-credential run. Before this existed the only record was a stdout line, so
   * from the dashboard a held task was indistinguishable from one the platform
   * had ignored.
   */
  NoUsableKeys: 'no_usable_keys',
  // `DependencyFailed: 'dependency_failed'` STOOD HERE AND IS DELETED (2026-08-29).
  // Its docblock claimed the dead-dependency sweep's write made "the terminal
  // record answer both questions at once: `failure_kind` says why it is failed,
  // this says what it was still waiting for when it died." The code cannot
  // provide either half of that.
  //
  //  - IT CANNOT BE READ. Both writers — `failTasksWithDeadDependencies` and
  //    `failTasksWithDeadPlanDependency` (`packages/db/src/queries/tasks.ts`) —
  //    set `status = 'failed'` in the SAME statement, and `normalizeTask` derives
  //    `holdReason: row.status === 'pending' ? row.holdReason : null` before
  //    `TaskSchema.parse`. So the value was written to be read and was null
  //    through the domain object on every row that ever carried it: 114 of 114 on
  //    prod, all `status = 'failed'`, zero `pending`. The only test that observed
  //    it had to bypass the read seam with a raw `db.select()` to do so.
  //  - IT ANSWERED NO SECOND QUESTION. Both writers stamp the SAME token into
  //    `failure_kind` and `hold_reason`, so all 114 rows read
  //    `dependency_failed / dependency_failed`. A duplicate of the column beside
  //    it is not a second fact.
  //  - NOTHING CONSUMED IT. {@link TaskStallKind.DependencyFailed} deliberately
  //    derives from the DAG instead, and says so in its own docblock.
  //
  // The 114 stored values stay in the column and are inert: the read seam nulls
  // them before the schema sees them, so no migration is needed and none should
  // be run. This is safe to delete only while no PENDING row carries the value —
  // true on prod, and true by construction once the two writers drop it, because
  // both transition the row to `failed` atomically. It is FALSIFIED by any path
  // that transitions a row to `pending` while `hold_reason` still carries the
  // deleted `'dependency_failed'` value, since such a row would re-enter the
  // domain through `normalizeTask` with the value the enum can no longer name.
  /**
   * The #513 same-area gate is holding this row: a non-`parallel_safe` sibling is
   * ACTIVELY BEING EDITED on the same `(account_id, repo_full_name)`, and neither
   * side declares a file set that PROVES the two touch different code — so no box
   * can claim this row until that sibling releases its box. Since #3158 the same
   * value also covers a `pr_review` held behind an ACTIVE review of the SAME pull
   * request (`prReviewSerialSql`): a PR never has two reviews running at once,
   * while reviews of different PRs on one repo no longer hold each other.
   *
   * Stamped and cleared by `markAreaHolds` (`packages/db`) out of the SAME SQL
   * fragment `claimNextReady` enforces the gate with, so the report can never name
   * a hold the claim does not apply, nor stay silent on one it does.
   *
   * This member answers the FALSE NEGATIVE half of one column being NULL, and
   * both halves are worth keeping straight because they look identical from the
   * dashboard. The false POSITIVE was a deep queue with a NULL here being READ as
   * this gate when a dead dependency was really holding it; that half is no
   * longer a hold reason at all — such a row is `failed`, not pending, so it is
   * answered by {@link TaskFailureKind.DependencyFailed} on the row itself and by
   * {@link TaskStallKind.DependencyFailed}, which the report DERIVES from the DAG.
   * (A `TaskHoldReason.DependencyFailed` existed for it until 2026-08-29 and was
   * deleted: it could never be read back, and it duplicated the failure kind.)
   * The false NEGATIVE is this member's own: measured 2026-08-23 the queue
   * genuinely WAS this gate — three boxes online, two idle, five rows pending on
   * one repo — and the column meant to say so was empty, while the lane arbiter
   * logged `lane: coding` for both idle boxes every 15s and placed nothing.
   */
  AreaBusy: 'area_busy',
  /**
   * The task's required capability (the `requires` token in its envelope) has no
   * online box provisioning it, so no runner can claim it.
   *
   * Stamped AND retracted by the capability-escalation sweep out of one pass over
   * the page it read (`markNoCapableBoxHolds`), so this column cannot outlive the
   * condition it names: the tick that finds a capable box online is the tick that
   * clears the row. Not "cleared when the task is claimed" -- that would leave the
   * dashboard asserting a cause that had already stopped being true for every row
   * still waiting its turn behind a queue.
   */
  NoCapableBox: 'no_capable_box',
  /**
   * This row names an INTERACTIVE origin (`scout` / `setup` / `architect` --
   * `originRequiresConversationalBox`) and the account has no ONLINE box that can
   * hold a mailbox, so no runner can execute it however long it waits.
   *
   * Stamped and retracted by `markNoConverseCapacityHolds` (`packages/db`) out of
   * the SAME fragment the claim narrows with, the discipline {@link AreaBusy}
   * already follows -- the report cannot name a hold the claim does not apply, nor
   * stay silent on one it does.
   *
   * It exists because CLOSING the reverse role gate created it. Before the gate, a
   * `worker` box claimed the session and failed it, which is visible as a failed
   * task; after it, the row is correctly left alone and would otherwise sit
   * `pending` with a NULL here -- indistinguishable from an empty queue, which is
   * the exact #1899 blind spot. A gate that refuses without saying so is not
   * better than the bug it replaced.
   */
  NoConverseCapacity: 'no_converse_capacity',
  /**
   * THE SERVICE GATE is holding this row: it is coding work on a repo whose
   * `.maintainer.yml` declares a `services:` block (#2919), and NO online box on the
   * account adverts one of the `service:*` tokens that declaration derives.
   *
   * It exists because CLOSING that gate created it — the same sentence
   * {@link NoConverseCapacity} carries, for the same reason. Before #2919 every box
   * claimed this work, the agent produced the correct edit, and the repo's own gate
   * discarded it in 60 ms for want of a Redis (#2918); after it, the row is correctly
   * left alone and would otherwise sit `pending` with a NULL here — indistinguishable
   * from an empty queue, which is the #1899 blind spot. A gate that refuses without
   * saying so is not better than the bug it replaced.
   *
   * Stamped and retracted by `markNoServiceCapacityHolds` (`packages/db`) out of
   * `noServiceCapacitySql`, which is built from the SAME `repoServicesRequiredCase`
   * fragment `claimNextReady` narrows with — so the report cannot name a hold the
   * claim does not apply, nor stay silent on one it does.
   *
   * It clears the moment a box adverting the token comes online, which is what makes
   * it different from an operator-pinned role: nobody has to remember to retract it.
   */
  NoServiceCapacity: 'no_service_capacity',
  /**
   * THE REPO-ONBOARDING GATE is holding this row: it is coding work
   * (`ORIGIN_EDITS_REPO`, `outcome_kind = 'pr'`) on a repo the onboarding pass has
   * never finished — we hold a `repositories` row for it and none of them carries
   * `ai_first_at` (`docs/idea/repo-onboarding.md`). No box can claim it until the
   * `dz/onboarding` PR merges or a maintainer declares the repo ready.
   *
   * Stamped and retracted by `markRepoNotOnboardedHolds` (`packages/db`) out of
   * `repoNotOnboardedSql` — the SAME fragment `claimNextReady` narrows with, the
   * discipline {@link AreaBusy} and {@link NoConverseCapacity} already follow, so
   * the report cannot name a hold the claim does not apply nor stay silent on one
   * it does.
   *
   * IT IS THE LARGEST UNEXPLAINED POPULATION THIS COLUMN EVER HAD, and it was
   * unexplained for the least interesting reason: the gate landed as a claim-side
   * `and not ${repoNotOnboardedSql()}` conjunct with a stall-report reader and no
   * WRITER, so the row was withheld silently. Prod 2026-09-01: 17 of the 23
   * pending rows carrying no reason at all, on ONE account with 10 boxes online
   * and 12 sibling rows the same empty-claim pass had stamped `area_busy` in the
   * same transaction. Nothing was broken; the verdict was simply never written
   * down.
   *
   * ABOVE {@link AreaBusy} and below {@link UpstreamGroupUnmerged}, taken from
   * {@link TASK_STALL_PRECEDENCE} rather than re-argued here — one ordering, one
   * place, so the stamped column and the derived report can never name different
   * causes for one row. DISJOINT from {@link NoConverseCapacity} by construction,
   * so their relative order is not a decision anybody has to make: this gate
   * excludes `NON_EDITING_ORIGINS` (which contains `scout` and `architect`) and
   * `setup` by its own conjunct, and those three ARE `INTERACTIVE_TASK_ORIGINS`.
   */
  RepoNotOnboarded: 'repo_not_onboarded',
  /**
   * THE PLAN-GROUP DAG's release rule is holding this row: it is a plan group's
   * dispatched task and an UPSTREAM group of that plan has not reached `merged`.
   * Legitimate ordering, and it still has to be sayable — the gate that held four
   * developerz-ai rows for ten days with nothing stored anywhere naming it.
   *
   * Stamped and retracted by `markUpstreamGroupHolds` (`packages/db`) out of
   * `upstreamGroupUnmergedSql`, the fragment `claimNextReady` NEGATES as a
   * conjunct — same one-fragment discipline as every member above.
   *
   * THE TOP of the hold-reason half of {@link TASK_STALL_PRECEDENCE}, and that
   * ordering is doing real work rather than being tidy: on prod 2026-09-01 ALL 5
   * rows this holds were ALSO {@link RepoNotOnboarded}, so a stamp order picked
   * per-writer would have had the column and `queueStallReport` naming different
   * causes for the same five rows — the drift the one-fragment rule exists to
   * prevent, one layer up.
   *
   * NOT the same fact as a pending `depends_on` sibling, which is deliberately no
   * member at all: that is a task waiting its turn behind other work, which the
   * queue depth already says, and {@link TaskStallKind.DependencyPending} says in
   * the derived report. This one is the CROSS-GROUP gate, released only by a
   * `plan_groups.status = 'merged'` that 548-dispatched-against-32-merged says
   * often never comes.
   */
  UpstreamGroupUnmerged: 'upstream_group_unmerged',
  /**
   * The account is AT ITS AI-DEV CAP (#3630): `entitlements().aiDevs` slots are
   * all running, so the assigner's concurrency gate (`admitsConcurrency`,
   * `apps/worker/src/dispatch/assigner.ts`) skipped this row on its pass — not
   * "no box", not "area busy": every box and key is fine, the SEAT is what is
   * missing. Stamped and cleared by the pre-claim writer `markTaskHolds`
   * (`packages/db`), the same generic account-scoped pass {@link NoUsableKeys}
   * rides, from the assigner when an account is at its cap and the tick a slot
   * frees.
   *
   * It exists because a seat-capped queue is the ONE hold whose remedy is a
   * purchase or a grant (a seat, or an operator `ai_devs` grant), and a row
   * held for it with a NULL here reads as "queued behind other work" — true, and
   * useless: the work ahead of it is never going to make room by itself while
   * the cap stays where it is.
   */
  SeatCapped: 'seat_capped',
  /**
   * THE UPSTREAM POOL IS COOLING (#3779/#3783): this account's shared router pool —
   * the BOX's own credentials, never a BYOK key (only a failure with no `key_id` arms
   * the record) — refused one of its boxes for a capacity/rate limit and the router
   * DATED the reset. Two writers, two row states (#5931,
   * `apps/worker/src/dispatch/pool-cooling-gate.ts`):
   *
   *   - ACCOUNT-WIDE, NULL window (`markTaskHolds`): a box that cannot be sent a sealed
   *     credential (`runnerSealsCodingCredential`)
   *     SUBTRACTS the account from its claim scope until then, rather than standing the
   *     whole box down. Everything below about the account-wide stamp is this state.
   *   - PER TASK, with `hold_until` = the end of the record's HARD hold
   *     (`markPoolCoolingHolds`): every other box keeps the account, and a claim that
   *     resolved NO credential — it would run on the walled box credentials — rolls
   *     back and parks that one row. A claim that resolved a BYOK key runs.
   *
   * Not "no key" —
   * {@link NoUsableKeys} is a pool that is out of CREDIT or cooled per key — and not
   * "no box": every box is online and idle, and the wall is upstream of all of them.
   *
   * IT EXISTS BECAUSE THE GATE SHIPPED WITHOUT A REPORT. The refusal was logged and
   * never stamped, so every row it held said one of exactly two untrue things: NOTHING
   * — the #1899 blind spot — or `area_busy`, a stamp an earlier pass wrote and no pass
   * retracted, because a cooling account's pass used to refuse before any `mark*Holds`
   * writer ran. Both were live on prod when this member was cut. Telling a maintainer
   * their repo's area is busy while the real wall is an upstream reset sends them to
   * free a box that changes nothing: the same blind spot with a confident wrong answer
   * instead of a silent one.
   *
   * NO TALLY IS QUOTED HERE, deliberately. Membership of that cohort is decided by a
   * Dragonfly key, so no SQL can re-derive it and any number written down becomes a
   * figure nobody can check — which is how one half of this change came to say "four"
   * while the other three said "five". The two SHAPES above are the claim; they are
   * what a reader can go and look for.
   *
   * Stamped and cleared by the generic account-scoped `markTaskHolds`
   * (`packages/db`), the same pre-claim pass {@link NoUsableKeys} and
   * {@link SeatCapped} ride. Its `held` write stamps OVER whatever is there
   * (`is distinct from`), which is what retires the five stale `area_busy` rows: a
   * pass refusing before the claim preempts any reason written by a pass that got
   * further — and is why {@link TaskStallKind.PoolCooling} outranks
   * {@link TaskStallKind.AreaBusy}, so the column and the derived report agree.
   *
   * THE ACCOUNT-WIDE STAMP IS A REPORT, NEVER A GATE: `claimNextReady` narrows its
   * candidate `where` on named fragments and NEVER on `hold_reason`, so a NULL-window
   * value withholds nothing. What refuses the claim is the assigner's own cooling
   * check, before the claim; clearing it by hand would change no dispatch decision.
   * The PER-TASK park is withheld by its WINDOW (`holdWindowLiveSql`, which reads
   * `hold_until` and never the reason), exactly like {@link CredentialCooling}; the
   * account-wide `markTaskHolds` pass neither stamps over nor clears a live window. Nothing may be built here that
   * assumes otherwise — a fragment added to the claim would also need a writer, and
   * `claim-gate-hold-writer.test.ts` is what decides that.
   *
   * THE PRECONDITION, stated because re-keying it back is a live hazard and not a
   * hypothetical: `markTaskHolds` carries an invariant from #1953 — `held` and
   * `admitted` may only carry accounts whose verdict does not depend on WHICH BOX
   * ASKED. The cooldown is therefore read on the TASK-OWNER's account. Keyed on the
   * asking box's account instead, two boxes drawing on one account (a donated box, a
   * user box fenced to its owner, a box whose own account is cooling while the task's
   * is not) reach opposite answers for the same row, and the per-account write
   * stamps and clears it every tick — each flip a real change, so the
   * `IS DISTINCT FROM` guard does not suppress it, and each flip re-emits the
   * account's whole pending set on the `tasks` SSE stream. That is the exact flap
   * #1953 exists to stop, and this member is only sound while the verdict is
   * box-independent.
   *
   * WHICH IS ALSO WHY ONLY TWO OF THE FOUR ANSWERS REACH THIS COLUMN. `poolGate.admit`
   * resolves one of `open` / `cooling` / `probing` / `probe_held`. The first two are
   * properties of the ACCOUNT — every box reaches the same one — so they are the
   * `admitted` and `held` halves of this stamp. The last two say WHICH BOX HOLDS THE
   * PROBE TOKEN, which is the box-dependent shape the paragraph above forbids: they
   * are logged and never stamped. Not an omission to be tidied up later — stamping
   * either is verbatim the flap #1953 exists to stop.
   *
   * THE RECORD IS TWO PARTS, and this column tracks only the first
   * (`apps/worker/src/dispatch/key-runtime.ts`):
   *
   *   - the HARD HOLD — the router's own quoted reset, floored at
   *     `POOL_COOLDOWN_FLOOR_SEC` and capped at `POOL_COOLDOWN_CAP_SEC`. Every box is
   *     refused (`cooling`), and this is the state that stamps.
   *   - then a HALF-OPEN BAND of `POOL_COOLDOWN_FLOOR_SEC` that the record outlives
   *     the hold by. Exactly one box TAKES the probe token (`probing`) and the rest
   *     wait (`probe_held`) — but AT MOST one actually reaches the pool, which is a
   *     weaker guarantee and a deliberate one. The token is taken at the pool gate,
   *     above the seat, usable-key and concurrency gates, and its Redis write is not
   *     part of the claim transaction, so the box that won it can still be refused
   *     below or roll its claim back; the window is then spent on nobody and the next
   *     one re-opens. The invariant is that no MORE than one box is admitted per
   *     window (`apps/worker/src/dispatch/key-runtime.ts`).
   *
   * So the stamp survives the HARD hold by at least one band, and through that band
   * the report is still honest: the account genuinely is held for every box but one.
   *
   * WHAT RELEASES IT IS A LATER PASS THAT REACHES THE GATE AND SEES `open` — NOT the
   * record expiring. This paragraph said "the record EXPIRING, and there is no second
   * answer", which was wrong in the direction that matters, and the sequence is
   * ordinary rather than exotic:
   *
   *   1. the account cools, and one pass stamps every pending row of it;
   *   2. its boxes go offline or stay busy. `listAssignableRunners` yields only
   *      ONLINE, IDLE boxes, so no pass is made for that account at all — and even a
   *      box that is offered exits the claim transaction at `boxGuard` or at a null
   *      `resolveCodingScope`, both of which sit ABOVE this gate;
   *   3. the Dragonfly record expires with nobody watching;
   *   4. nothing clears the column, because the only clear is `admitted` on a pass
   *      that got as far as `poolGate.admit`.
   *
   * {@link TaskStallKind.NoBoxOnAccount} does NOT mask that row. Its predicate
   * excludes only `revoked` runners, so an OFFLINE box still counts as a box; it
   * answers the zero-box case only once the runner rows are actually revoked. And
   * this member outranks {@link TaskStallKind.AreaBusy}, so the stale stamp wins the
   * report outright.
   *
   * AND A STALE STAMP IS ABSORBING, which is what makes this worse than a wrong
   * label. THE PROPERTY: of the hold writers in `queries/tasks/claim.ts`, only
   * `markTaskHolds` and `markNoCapableBoxHolds` stamp with
   * `hold_reason IS DISTINCT FROM <reason>` — every other one stamps `where
   * hold_reason is null` (or over a lapsed `error_issue_pending`, `errorIssueHoldLapsedSql`;
   * `markAreaHolds` also over a `key_saturated` / `key_reserved` park, in TWO named arms —
   * a LAPSED one, `keyParkLapsedSql` (#6892), and a STALE one, `keyParkStaleSql`, whose
   * window is still live but whose key reasoning the current area verdict supersedes),
   * namely `markAreaHolds`, `markNoConverseCapacityHolds`, `markNoServiceCapacityHolds`, and the shared `markHolds` body behind
   * {@link RepoNotOnboarded}, {@link ModeDisabled}, {@link UpstreamGroupUnmerged},
   * {@link BoxFaultExcluded} and {@link NoRoleMachine}. So a row still carrying a dead `pool_cooling` cannot be
   * corrected by any of them: it is stuck on the one reason that is no longer true,
   * while the remedy for it reads "nothing to do". That is the confident-wrong-answer
   * failure the `area_busy` mislabel motivated, pointing the other way — which is why
   * {@link TaskStallKind.PoolCooling}'s remedy names this case out loud instead of
   * promising the hold lifts by itself. The partition is decided from the source by
   * `queries/hold-writer-overwrite.test.ts`, so it is checked rather than recited.
   *
   * NO REAPER EXISTS FOR A HELD PENDING ROW, here or anywhere. The bounded TTL is
   * what makes the HOLD safe; it is not what makes the STAMP truthful, and nothing
   * keyed to this member may assume the two expire together. A record with no expiry
   * is treated as probeable rather than as a hold for the same reason: the
   * alternative is an account held with nothing to release it.
   */
  PoolCooling: 'pool_cooling',
  /**
   * The box that last refused this task for a red baseline is excluded from it for
   * `BOX_FAULT_EXCLUSION_MINUTES` (`./failure-budget`); other boxes may claim it (#3094).
   *
   * The stamp is ACCOUNT-scoped (`markBoxFaultExclusionHolds`), so it also lands on a
   * row another box could still take: it records that the refusing box was turned
   * away, and the next claim by any other box moves the row out of `pending`.
   *
   * The refusal is `box_baseline_red`, an UNCHARGED kind, so the row re-enters the
   * queue at zero attempts spent; without the exclusion the same box reclaims it and
   * re-runs a full paid session to rediscover the same red base. THAT exclusion
   * lapses on a clock, never on an eligibility re-check, so a task whose only
   * capable box refused it for a red baseline is delayed, not stranded. What ends a
   * task every box refuses that way is the budget's `maxConsecutive`, not this hold.
   *
   * THE SECOND CONDITION (#6237) HAS NO CLOCK. A box that could not DECODE the task's
   * assignment leaves it held under this same value, uncharged, for a box on a runner
   * release published after the one that refused (`tasks.decode_refused_release_at`,
   * `@developerz/db` `queries/tasks/decode-hold.ts`). It is written by the box's report
   * (`holdUndecodableTask`), not by `markBoxFaultExclusionHolds` alone, and it clears
   * only when a newer box claims the row or the row is ended as a bad envelope. On an
   * account whose boxes never update it does not lapse: that row IS stranded until
   * someone updates a box, and `cron.stalled_queue` is what says so. It shares this
   * value because a new member of a stored closed set is unreadable to every pod a
   * release behind (`packages/db/docs/migration-rules.md`), which is the incident the
   * hold exists for.
   *
   * THE THIRD CONDITION (#7081) HAS NO CLOCK EITHER. A task whose gate died
   * `gate_memory_unmet` at a per-task cap (`tasks.memory_unmet_cap_bytes`) is offered only
   * to a box whose advertised cap is larger (`taskCapFits`, `./gate-memory-cap.ts`); while NO
   * box that can take new work for its account is, `markBoxFaultExclusionHolds` stamps this
   * value and `task stalls` names the cap it needs, the largest online and that a bigger box
   * is the fix (`memoryShortRemedy`). It clears the pass such a box is online. Same value,
   * same reason as the decode hold.
   */
  BoxFaultExcluded: 'box_fault_excluded',
  /**
   * THE AGENT-MODE GATE is holding this row (plan 101 slice 06): the task's mode
   * (`AGENT_MODE_KEY_FOR_ORIGIN`, the switch that holds its origin) resolves DISABLED for
   * its repo (repo `agent_modes` row > org row > the enabled code default).
   *
   * A reversible HOLD, never a throw at claim and never a terminal failure: a throw
   * after the claim rolls the row back to `pending` and the next tick claims it
   * again (a livelock), and failing the row would turn an operator's switch into
   * lost work. It clears the tick the mode is re-enabled.
   *
   * Stamped and retracted by `markModeDisabledHolds` (`packages/db`) out of
   * `modeDisabledSql()` — the SAME fragment `claimNextReady` narrows with, whose
   * origin→mode `CASE` is pinned to `AGENT_MODE_KEY_FOR_ORIGIN` by a test, so the report
   * cannot name a hold the claim does not apply nor stay silent on one it does.
   */
  ModeDisabled: 'mode_disabled',
  /**
   * A REVIEW or review-thread REPLY — the credential-reading lanes with no box-env
   * floor; scout, architect and setup run on the box's own env instead and are never
   * held here (review BLOCK B2) — found no key it may use RIGHT NOW, and a key the
   * provider only TEMPORARILY refused (`credits_exhausted` / `rate_limited`, each
   * carrying `byok_keys.verification_retry_at`) WOULD serve it once it recovers. The
   * row waits until `tasks.hold_until` — the earliest such key's reset — instead of
   * shipping with no credential (plan 101 K3).
   *
   * A CODING-LANE task pinned to a model by its agent template lands here too (plan
   * 102 slice 07b), but ONLY when the one key that serves the pin is cooling in the DB
   * (`byok_keys.verification_retry_at`); it then waits for that key's retry exactly
   * like a review. A key held in a Redis cooldown or the skip set stamps NO hold: the
   * claim rolls back as `all_cooled` (`PoolExhaustedError`) and is claimed again once
   * the cooldown or skip expires. A pin that NO key serves is not held; it runs on the
   * box env.
   *
   * WHY IT EXISTS: those keys are outside `listPoolKeys` (`status = 'valid'` only),
   * so the pool read as EMPTY and the resolver answered `no_pool`, the benign arm —
   * the box ran with no model and a review lane POSTED a floor review. Measured
   * 2026-09-21: 157 `no_pool` review dispatches over 10 days on an account whose
   * only live keys were `credits_exhausted`, part of 136 of 160 reviews in 24h
   * posted blind.
   *
   * PER TASK, not per account, and that is the design. The verdict is exact only
   * once the task is in hand (its lane fence, its tier, its dispatcher's personal
   * keys), so the resolver decides it at claim time, the claim rolls back, and the
   * assigner parks THIS row with a `hold_until` the claim skips
   * (`markCredentialCoolingHolds`). An account-wide hold would stop the lanes that
   * run on the box's own env and need no key; a plain rollback (the `all_cooled`
   * path) would reclaim the same row every tick and starve the account's queue
   * behind it — the livelock {@link ModeDisabled} names.
   *
   * It lapses on a CLOCK, like {@link BoxFaultExcluded}'s box-fault exclusion (not its
   * decode hold, which has none): past `hold_until` the row
   * is claimable again and the resolver re-decides it — a recovered key serves it,
   * a still-cooling one re-parks it on the new reset. A key on the account turning
   * `valid` releases it EARLY (`releaseHoldsOnKeyValid`, plan 102 slice 03): the
   * window is a provider's stated reset, up to 31 days out, and a fresh key must
   * not wait behind it.
   */
  CredentialCooling: 'credential_cooling',
  /**
   * A REVIEW (`pr_review`, or a `pr_review_reply` on its thread) that no model could run: the resolver found no key that
   * serves it and none cooling that will (`no_pool`, `lane_fenced`,
   * `models_parked`), so it is PARKED instead of dispatched with no credential — a
   * review has no box-env floor, and the floor review it used to post read as a
   * real verdict (136 of 160 reviews in 24h, 2026-09-21; plan 101 K4). The box's
   * own refusal (`TaskFailureKind.ReviewNoModel`) re-pends onto the same hold.
   *
   * Stamped per task with a `hold_until` by `markReviewAwaitingModelHolds` — after the
   * rolled-back claim (`claimAndOutbox`) or on the failure write — and the claim
   * skips it while that window is live, exactly as {@link CredentialCooling}.
   *
   * WHAT WAKES IT: a key transition to `valid` on the account (`billing.key_verified`,
   * fired by a verify and by the provider-reconcile restore) releases every parked
   * review of the account at once; `REVIEW_NO_MODEL_RECHECK_MS` (`./failure-budget`)
   * is the backstop for key changes that fire no signal. On release the resolver
   * re-decides: a key serves it, or it parks again.
   */
  ReviewAwaitingModel: 'review_awaiting_model',
  /**
   * The provider REFUSED THE ACCOUNT'S KEY on this task's last run — its quota is
   * spent (`TaskFailureKind.ProviderQuotaExhausted`) or the credential is dead
   * (`TaskFailureKind.ProviderAuthInvalid`) — so the row is PARKED rather than
   * failed (plan 101 L3). Both are account conditions: nothing about the work was
   * graded, and what clears them is a KEY — funded, restored, re-keyed — never a retry.
   *
   * WHY IT EXISTS: both kinds were `Charged` at one attempt, so a spent key failed the
   * account's backlog ONE TASK AT A TIME — 446 `provider_quota_exhausted` rows over the
   * 30 days to 2026-09-21, 227 of them on one account on 2026-09-05 — each of which
   * would have run fine once the key was funded.
   *
   * NOT {@link NoUsableKeys}, and the reason is mechanical, not taste: that member is a
   * REPORT stamped and retracted every tick by `markTaskHolds`, `claimNextReady` never
   * reads `hold_reason`, and `tasks_hold_until_ck` admits no window beside it — so a
   * re-pend stamped with it would gate nothing and an uncharged kind would hot-loop.
   * This member is WINDOWED, like {@link ReviewAwaitingModel}: stamped per task with a
   * `hold_until` by `failOrRetryTask`'s held re-pend (`taskFailureRependHold`,
   * `./failure-budget`), and the claim skips it while that window is live. That write
   * is its ONLY stamp. No dispatcher pass writes it, because only a box's failure
   * report knows the key was refused.
   *
   * WHAT WAKES IT: a key transition to `valid` on the account releases every row parked
   * here at once; `PROVIDER_KEY_REFUSED_RECHECK_MS` (`./failure-budget`) is the
   * backstop for the key changes that fire no signal — a box's own backend key
   * replaced, a weekly window resetting. A change to the `llm_routes` row its lane
   * routes on releases it too (`releaseHoldsOnRouteChange`): the refused key was the old
   * selection's. On release the task runs again: a working key serves it, a
   * still-refused one parks it again at zero attempts spent.
   */
  ProviderKeyRefused: 'provider_key_refused',
  /**
   * The provider (or the shared router pool in front of it) refused this run for
   * CAPACITY — a `provider_rate_limit` grading, the hot-loop defect this member
   * closes: the kind is `Uncharged`, so its re-pend used to be IMMEDIATELY
   * claimable and one task re-ran 83 times at attempt 0 between two box cooldowns.
   * Not {@link ProviderKeyRefused}: the KEY is fine and no key change releases
   * this — the wall is the pool's recovery window, stated in the refusal itself.
   * Since #6501 (2026-10-19) a `provider_transient` re-pend parks here too — the same
   * 83-runs loop at fleet width, 1,699 runs of five tasks in a week. And a
   * `screen_unavailable` one (2026-10-19): the intake screen's call failed on a pool that
   * still serves, which is this fact, not "no screen key" (PR #6574's review).
   *
   * WINDOWED, exactly like {@link ProviderKeyRefused}: stamped per task with a
   * `hold_until` by `failOrRetryTask`'s held re-pend (`taskFailureRependHold`,
   * `./failure-budget`) — the provider's trusted stated reset when the refusal
   * quotes one, else {@link PROVIDER_CAPACITY_RECHECK_MS} as the backstop (for
   * `provider_transient`, a window doubling with the uncharged streak) — and
   * the claim skips it while that window is live (the claim gates on the WINDOW,
   * which `tasks_hold_until_ck` now admits beside this member). That write is its
   * ONLY stamp. No dispatcher pass writes it, because only a box's failure report
   * knows the pool refused.
   *
   * WHAT WAKES IT: the clock, and a route change. A lapsed window is claimable and the
   * run is re-graded against the pool as it then is — served, or parked again on the
   * fresh refusal. A change to the `llm_routes` row its lane routes on releases it at
   * once (`releaseHoldsOnRouteChange`): under an Agents-page selection the refusal was
   * the OLD selection's key, and the lane now runs on another. `uncharged_streak` still reads every refusal, so a pool that
   * stays closed keeps counting even though `attempt` never moves.
   */
  ProviderCapacity: 'provider_capacity',
  /**
   * The repo's own `bin/setup` is DOWN ON EVERY BOX: it refused with one signature on
   * `REPO_ENV_FAULT_MIN_BOXES` distinct boxes, from `REPO_ENV_FAULT_MIN_SOURCES` distinct
   * tasks or PRs, inside `REPO_ENV_FAULT_WINDOW_MINUTES` (`./repo-env-fault`), so the fault is the repo's environment — a bloated base DB, a
   * registry answering 401 — and another box would only spend another attempt on it.
   * Only rows whose lane runs setup are held (`ORIGIN_RUNS_REPO_SETUP`); reviews,
   * replies and scouts keep moving.
   *
   * WINDOWED, like {@link ProviderKeyRefused}: the claim skips the row while
   * `hold_until` is live. Two writers stamp it. `failOrRetryTask`'s held re-pend
   * (`taskFailureRependHold`) parks the row whose setup just refused — and, since F7
   * (2026-10-19), a row whose verify failed on the BASE (`box_baseline_red`): the same
   * fact family, the repository's own state on every box, on a streak backoff and with no
   * fault row behind it (its wakes are below). The repo-env
   * sweep (`holdRepoEnvFaultTasks`, `packages/db`) parks every OTHER pending setup-running
   * row of the repo and re-arms each window every tick while the fault stands.
   *
   * WHAT WAKES IT: the sweep. It releases ONE row as a probe per
   * `REPO_ENV_PROBE_INTERVAL_MS` (at once after a push to the default branch), and the
   * first setup that passes on a tree the fault affected (a ref it refused, or the probe)
   * clears the fault and releases every row it held
   * (`releaseRepoEnvFaultHolds`). `REPO_ENV_DOWN_HOLD_MS` is the fail-safe clock if the
   * sweep stops.
   *
   * A `box_baseline_red` park has no fault row, so the sweep's probe never reaches it. It
   * wakes on a PUSH TO THE REPO'S DEFAULT BRANCH that landed after it parked, the event that
   * can turn the base green: the same minutely sweep releases it (`releaseBaselineRedHolds`,
   * `packages/db`, with its audit row in one transaction, `apps/worker`
   * `cron/baseline-red-release.ts`). Otherwise its own clock, the streak backoff
   * (30 min growing to 24 h), lapses and the claim re-runs it on the base as it then is.
   */
  RepoEnvFault: 'repo_env_fault',
  /**
   * PARKED, NOT FAILED (2026-09-24, `./resume-backoff`): a coding task spent its attempt
   * ladder with work saved on its own branch, so it waits `hold_until` and then resumes that
   * work with a fresh, small attempt window. Stamped by the failure write
   * (`failOrRetryTask`'s park step, `parkExhaustedTask`); retracted by the claim when the
   * window lapses, or by an operator `task redispatch`, which resumes it at once.
   */
  ResumeBackoff: 'resume_backoff',
  /**
   * EVERY pool key that could serve this task is AT ITS CONCURRENCY CAPACITY
   * (capacity-aware scheduling): each is already running as many model streams as
   * its plan sustains — the owner's DECLARED `byok_keys.capacity`, else the
   * platform's default for its kind and provider, pulled lower by what telemetry
   * LEARNED (`effectiveKeyCapacity`, `../../value-objects/key-capacity`). So the
   * task waits for a slot instead of piling one more stream onto a plan that cannot
   * run it: one z.ai coding sub runs one PR at a time, and a second stream on it is
   * a provider 1302 for both.
   *
   * NOT {@link CredentialCooling}: every key here is healthy and signing; none was
   * refused. The wall is the account's own in-flight work, which frees as it
   * finishes. And NOT {@link SeatCapped}: that is the account's AI-dev seats, this is
   * the plan behind the KEY, and an account can hit either without the other.
   *
   * WINDOWED, like {@link CredentialCooling} and for its reason: the verdict is exact
   * only with the task in hand (its lane, tier and fence decide which keys could
   * serve it, and each key's in-flight units live beside the claim), so the resolver
   * decides it at claim time, the claim rolls back, and the assigner parks THIS row
   * (`markKeySaturatedHolds`) with a `hold_until` BACKSTOP (`KEY_SATURATED_BACKSTOP_MS`,
   * 10 min) — not a provider's reset, because a slot frees whenever a running task ends
   * and nothing about that is dated in advance. A plain rollback would reclaim the same
   * row every tick and starve the queue behind it — the livelock {@link ModeDisabled}
   * names. ONLY a physically full key parks here: a unit that was free but held for
   * better-ranked work parks {@link KeyReserved} instead.
   *
   * WHAT WAKES IT (#6704): ROOM, first. The claim reads a once-per-pass key-room snapshot
   * and a key-full park is claimable the moment one of its keys has a free unit, bounded
   * to as many parks per key as it has free units (`holdLiveSql`, `@developerz/db`). With
   * no snapshot the park is due 2 minutes after it was written, never later than before
   * room gating. Then a KEY-POOL change (`releaseKeyFullParks`): a key turning `valid`, a
   * capacity or purpose set. Then a change to the `llm_routes` row its lane routes on
   * (`releaseHoldsOnRouteChange`): the keys it waited on were the OLD selection's. The
   * backstop bounds whatever none of those observed.
   *
   * WHICH KEYS: the park records the full keys it named (`tasks.hold_key_ids`). Under
   * an Agents-page selection those are the selected key(s) alone — other keys may be
   * idle, and the task is not handed to them. The record is not what bounds the lane's
   * reservation, though: a stamp is absent before the first park and after the
   * claim clears it, so capacity admission derives a bound lane's keys from its
   * `llm_routes` row at read time (`waitingLaneKeyIds`, `@developerz/agent`).
   */
  KeySaturated: 'key_saturated',
  /**
   * A FREE SLOT WAS HELD FOR HIGHER-PRIORITY WORK (owner ruling, #6704 comment 6305740671):
   * a key that could serve this task has room, but the 2026-09-24 finish-before-starting
   * ruling holds that unit for a better-ranked waiter (`reservedForHigherRanks`,
   * `../../value-objects/key-capacity-rank`). Split from {@link KeySaturated}, whose keys
   * are physically full, so the two waits read apart.
   *
   * Read like {@link KeySaturated} everywhere it is read: WINDOWED, it waits on the keys its
   * park stamped (`tasks.hold_key_ids`), it keeps reserving for the work that outranks it,
   * and it is released by the clock and by a route change (`releaseHoldsOnRouteChange`),
   * never by key room. The stall report folds it under the `key_saturated` kind.
   *
   * WRITTEN BY `markKeyReservedHolds` (#6704), the release after the one that taught every
   * reader the value (#6711, `docs/standards/stored-closed-sets.md`): admission refuses a
   * claim this way when the unit it would take is free but held back (`key-admission.ts`,
   * the post-reservation verdict), and parks the row `KEY_SATURATED_RECHECK_MS` (2 min).
   * Released by time and a route change only: a room release would put it back at the head
   * the moment a unit freed, and the same reservation would refuse it again.
   */
  KeyReserved: 'key_reserved',
  /**
   * A PERSON IS PUSHING to the pull request this `pr_babysit` round works on, so the round
   * waits for the branch to go quiet (`../babysit/human-quiet`) instead of starting on a
   * head that is about to move — or re-starting on the next one after the head watch
   * stopped it.
   *
   * WINDOWED, like {@link ProviderCapacity}: stamped by `failOrRetryTask`'s held re-pend
   * (`taskFailureRependHold`) when the box reports `human_pushing`, with `hold_until` the
   * instant the box read off the PR — the person's last push plus `HUMAN_PUSH_QUIET_MS` —
   * and the claim skips the row while that window is live. That write is its ONLY stamp:
   * only a box reading the PR's commits knows who pushed.
   *
   * WHAT WAKES IT: only the clock. Past `hold_until` the row is claimable and the box asks
   * again — quiet, and the round runs; pushed to again, and it parks on the new window. A
   * PR kept busy past `HUMAN_PUSHING_SURFACE_MS` is surfaced on `next_steps`.
   */
  PrQuietWindow: 'pr_quiet_window',
  /**
   * THE PULL REQUEST'S HEAD HAS NOT SETTLED CI, so its review waits (#6718, owner ruling
   * 2026-10-20: a review starts when CI is green, like a human's). The task is MINTED and
   * held, never withheld from the mint: a `pending` review is what the merge gate's
   * `review_in_flight`, the supersede, the retire-on-close and the mint's dedup all key on,
   * so not minting it would open a merge race on the very head nobody reviewed.
   *
   * WINDOWED. Three stamps, one meaning, all pending:
   *   - the review mint (`apps/worker`), when the head's CI has not settled: `holdReviewForCi`
   *     (`packages/db/src/queries/tasks/ci-pending.ts`), `hold_until` = the head's CI ceiling
   *     (`reviewCiHoldUntil`: first sighting + `CI_WAIT_CEILING_MS`, else now + it);
   *   - the box at claim, when it reads CI still pending on the head: it ends the run
   *     `review_ci_pending` (uncharged) and `failOrRetryTask`'s held re-pend (`taskFailureRependHold`)
   *     parks the row here until the instant the box stated (`reviewCiPendingMessage`, the
   *     same ceiling), else `CI_WAIT_CEILING_MS` from now;
   *   - the CI wake's RE-HOLD (`apps/worker/src/review/ci-wake-writes.ts`), when the head's
   *     CI is PENDING again after the review was cancelled `ci_red`: ONE statement puts that
   *     corpse back to `pending` AND parks it here — `reviveCiRedReview` with `holdUntil`
   *     (`packages/db/src/queries/tasks/ci-pending.ts`), to the verdict's own CI ceiling
   *     (`recheckAt`) — committed in ONE transaction with its `review.ci` wait row (a
   *     `policy.gate.refused` row, decision `held_for_ci`) and its `task.reenqueued` row. So
   *     the RE-RUN's verdict decides it — green releases the hold, still red cancels the
   *     review again — and the row waits for CI, never for a box.
   *
   * WHAT WAKES IT: a CI completion on that head, or a CI re-run. The worker re-grades the
   * head: it releases the hold (`releaseCiPendingHold`: green, or no CI), cancels the review
   * `ci_red`, or re-holds a `ci_red` one whose head's CI is pending again (the third
   * stamp). For heads that send no completion event (a fork PR's checks, status-only CI)
   * the worker's server-side sweep is the fallback poll. The clock, `hold_until`, is NOT a
   * wake: it is the CI ceiling, where the box reviews anyway (`timed_out`, disclosed), so a
   * box claims the row only to review it — never to re-read CI (prod 2026-10-22: a 5-minute
   * window cost one box claim per lapse, 22 on one review).
   *
   * NO STALL KIND: the wait is self-clearing and bounded by the ceiling, owned by CI, the
   * sweep and the clock; a stall report naming it would page nobody who can act.
   */
  CiPending: 'ci_pending',
  /**
   * HELD, NOT FAILED (2026-09-29, `./env-fault-hold`): the task spent its whole attempt ladder
   * on a failure kind whose deaths never GRADED the change and blame the box
   * (`taskFailureHoldsOnExhaustion` — `gate_unrunnable`: the repo's gate exited 75, or could
   * not execute, on every box it reached). Ending it would cascade-fail its dependents and
   * stall its plan over a box nobody provisioned, so the failure write keeps it `pending`
   * here with a blocker carrying the gate's refusal line and the release.
   *
   * WINDOWED for `ENV_FAULT_HOLD_MS` — the life of its blocker — so the claim skips it
   * and every `mark*Holds` pass leaves it alone (a live window is never stamped over).
   * Stamped by the failure write's hold step (`holdExhaustedTask`, in the same transaction
   * as `failOrRetryTask`, so no reader sees the row `failed` in between).
   *
   * WHAT WAKES IT: an operator `task redispatch`, which re-queues it with a fresh ladder and
   * withdraws the blocker; or the window, after which ONE run is let through (the ladder is
   * still spent, so a second refusal holds it again at once — one paid session per window,
   * never a loop).
   */
  EnvFaultExhausted: 'env_fault_exhausted',
  /**
   * THE LANE'S AGENTS-PAGE SELECTION CANNOT RUN NOW (owner ruling 2026-10-02): the
   * task's lane is bound to an explicit `llm_routes` row, and every key that row names
   * is cooling, stepped aside after a refusal, or has refused run after run
   * (`SelectionWaitingHold`, `apps/worker` `dispatch/route-bound-hold.ts`). The task
   * waits for the SELECTED key; it is never handed to another one.
   *
   * NOT {@link CredentialCooling}, and the difference is the account's other keys: that
   * member says no key at all can serve the task, which is an account KEY OUTAGE
   * (`KEY_OUTAGE_TASK_HOLD_REASONS`) with a "work is paused" notice. Here the rest of
   * the pool may be healthy and idle — the wait is the owner's own choice of key, and
   * what ends it early is choosing another on the Agents page. So this member is absent
   * from the outage membership by construction.
   *
   * WINDOWED: stamped per task with a `hold_until` by `markSelectionWaitingHolds` after
   * the rolled-back claim, and the claim skips it while that window is live. The window
   * is the cooldown floor for a key that stepped aside, the provider's stated reset for
   * one cooling in the DB, and `SELECTION_FAILING_RECHECK_MS` once the selected key has
   * refused `SELECTION_FAILING_AFTER` runs in a row (`./selection-wait`).
   *
   * WHAT WAKES IT: a change to the `llm_routes` row its lane routes on
   * (`releaseHoldsOnRouteChange`), a key on the account turning `valid`
   * (`releaseHoldsOnKeyValid`), or the clock.
   */
  SelectionWaiting: 'selection_waiting',
  /**
   * THE RUN-START SCREEN HAS NO KEY TO RUN ON (plan 101 09-10; owner-delegated ruling
   * 2026-10-08). Every lane screens third-party text against the account's own
   * NON-DONATED pool — the envelope at run start (`withTaskTextScreen`, `apps/runner`),
   * and for the review lanes their own live text in-lane through the same
   * `screen_items` responder (`fleet/screen-items.ts`, `apps/worker`) — so this gate
   * holds review claims too. With none — no key, only donated keys, every key out of
   * the pool — each item comes back `unavailable`, the run fails `screen_unavailable`
   * uncharged, and three in a row end the task: a box spent per run, and the task
   * lost, for a fact the claim could see.
   *
   * WINDOWED, like {@link CredentialCooling} and for its reason: the verdict needs the
   * task in hand (every origin screens — the envelope at run start, the review lanes'
   * own text in-lane), so the claim
   * decides it after the credential resolve, rolls back, and the assigner parks THIS row
   * (`markNoScreenKeyHolds`) with a `hold_until` the claim skips. A plain rollback would
   * re-claim the same row every tick — the livelock {@link ModeDisabled} names.
   *
   * WHAT WAKES IT: a key on the account turning `valid` (`releaseHoldsOnKeyValid`), or
   * the clock (`NO_SCREEN_KEY_RECHECK_MS`, `./failure-budget`). On release the claim
   * re-decides: a screen key serves it, or it parks again.
   *
   * A SECOND WRITER (2026-10-18): `failOrRetryTask`'s held re-pend of a run the mothership
   * regraded `screen_key_unusable` — no key on the account could serve the screen as it
   * failed. Same reason, because the same event wakes it; its window is
   * `SCREEN_UNAVAILABLE_RECHECK_MS`, uncapped, so an outage of the account's keys waits
   * here instead of ending the task. A plain `screen_unavailable` (the pool still serves)
   * parked here from 2026-10-09 and moved to {@link ProviderCapacity} on 2026-10-19: the
   * row said "no screen key" on accounts with working keys.
   */
  NoScreenKey: 'no_screen_key',
  /**
   * THE ERROR'S PUBLIC ISSUE IS NOT FILED YET (#6225, owner rulings on that issue). A task
   * minted for a Sentry error group waits for the error-issue lane (#5653) to file the group's
   * public issue and write its number onto the task's envelope (`issue_number`), so the
   * runner's PR footer renders `Closes #n` and the first fix PR to merge closes that issue.
   *
   * WINDOWED, by `ERROR_ISSUE_HOLD_MS` from the mint (`./error-issue-hold`): the claim skips
   * the row while the window is live, and a lapsed window lets the task dispatch unlinked,
   * with no sweep. Its stamp is the triage mint, on a task whose raw task has an
   * `error_signature` and a `sentry_issue` origin; its retraction is `linkErrorIssueTasks`
   * (`packages/db`), which the lane calls on a filing (with the number) and on a cap, a
   * refusal or a fault (without one), and which skips a row whose window already lapsed.
   *
   * A LAPSED STAMP IS NOT A LIVE REASON: once the window passes the claim stops skipping the
   * row but nothing clears the stamp until a box takes it. On a row that stays `pending` for
   * another reason, the otherwise-NULL-only writers (`markAreaHolds`, `markNoConverseCapacityHolds`,
   * `markNoServiceCapacityHolds`, `markHolds`) treat a lapsed `error_issue_pending` as free to
   * restamp (`errorIssueHoldLapsedSql`), so the true reason is written down. A LIVE window is
   * never overwritten — with ONE named exception, and it is not this member: a key park
   * (`key_saturated` / `key_reserved`) may be restamped by `markAreaHolds` even while its own
   * window is still live, because the park was the furthest a pass got BEFORE the area went
   * busy and the current pass's area verdict supersedes its key reasoning. That pair gets
   * TWO narrower exceptions of the same shape, both on that ONE writer and neither on a
   * plan-admissible row: a LAPSED key park (`keyParkLapsedSql`, #6892) and a STALE one
   * (`keyParkStaleSql`), which the `task.held` audit row records as a transition
   * (`from: key_saturated | key_reserved`, `to: area_busy`) rather than a first stamp. No
   * other windowed reason is restamped by a NULL-only writer.
   */
  ErrorIssuePending: 'error_issue_pending',
  /**
   * EVERY ONLINE MACHINE HAS THIS TASK'S ROLE SWITCHED OFF (#6552, "roles on machines"): the
   * org/repo answer lets the role run, but each online box that could take the row carries a
   * machine-scope `agent_modes` row with `enabled = false` for the task's role. A machine
   * switch is ANDed with the org/repo setting and never re-enables an org's or a repo's
   * "off", so this is a separate claim conjunct, never inside `modeDisabledSql` (#1953).
   *
   * Shipped readers first (#7008); its writer is `markNoRoleMachineHolds` (#6756), over the
   * box-independent `noRoleMachineSql()`, and `TaskStallKind.NoRoleMachine` is derived from
   * that same fragment. The claim enforces the box-local half, `machineRoleOffSql`. No window:
   * it stamps no `hold_until`; nothing releases it until someone turns a switch back on.
   */
  NoRoleMachine: 'no_role_machine',
  /**
   * THE TASK'S EPIC IS PAUSED (#6748, epic #6552): its plan group belongs to a plan with
   * `plans.paused_at` set, so the claim holds it with the running slices (owner ruling,
   * ivndev001, 2026-10-20: "pausing an epic holds the running slices AND the waiting
   * slices"). Written by `markPlanPausedHolds`, account-wide like `markUpstreamGroupHolds`,
   * over `planPausedSql()`, the fragment the claim reads. A PERSON'S decision, released only
   * by Resume, so it outranks {@link UpstreamGroupUnmerged}: a paused slice usually also has
   * an unmerged upstream, and naming that would send the reader to the wrong remedy. No
   * window.
   */
  PlanPaused: 'plan_paused',
  /**
   * THE TASK IS PINNED TO ITS OWN MACHINE (#6748): a paused task resumed `pending` with
   * `pinned_runner_id` set, so only that machine may claim it (it saved its work there).
   * Written by `markMachinePinnedHolds` over the box-independent `taskPinnedSql()`, account-
   * wide like every empty-claim writer (#1953: a per-account stamp never carries a verdict
   * that depends on which box asked); the claim enforces the per-box `<> runner` half
   * inline. Transient: the machine takes it on its next pass, or the reaper, the drain or
   * the offline backstop clears the pin, and the stamp is retracted once the pin is gone.
   */
  MachinePinned: 'machine_pinned',
} as const;
export const TaskHoldReasonSchema = z.enum(TaskHoldReason);
export type TaskHoldReason = z.infer<typeof TaskHoldReasonSchema>;
